import type { ElementType, ReactNode } from 'react'
import { useInView } from './useInView'

/**
 * Vozes e degraus da anatomia (cap. 06/09; ADR-009). Cada componente emite seu data-anatomy para os gates de browser.
 * Nada aqui aceita cor, tamanho ou peso por prop: tudo vem da escala-enum via classes (src/brand/brand.scss).
 */
const cx = (...c: Array<string | false | undefined>) => c.filter(Boolean).join(' ')

/** Kicker — 11/mono, caixa alta, +8% de entreletra, na cor de ação: "diz onde você está" (p.081). */
export function Kicker({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx('z-kicker', className)} data-anatomy="kicker">{children}</p>
}

export type TituloVariant = 'editorial' | 'manifesto' | 'tela' | 'h2'
/** Título — editorial (Newsreader Light 44), manifesto (64), tela (Schibsted SemiBold 32) ou h2 (Medium 22). Uma ou duas linhas, nunca três. */
export function Titulo({ children, variant = 'editorial', as, className }: { children: ReactNode; variant?: TituloVariant; as?: ElementType; className?: string }) {
  const Tag: ElementType = as ?? (variant === 'h2' ? 'h2' : 'h1')
  return <Tag className={cx('z-titulo', `z-titulo--${variant}`, className)} data-anatomy="titulo">{children}</Tag>
}

/** Régua do trilho — 56 × 2, na cor de ação, separa o título do que vem depois (p.083). */
export function Regua({ className }: { className?: string }) {
  // fallback para navegadores sem animation-timeline: a classe entra/sai com a viewport (0% ≡ 100%)
  const [ref, inView] = useInView<HTMLSpanElement>({ once: false })
  return <span ref={ref} className={cx('z-regua', inView && 'is-in-view', className)} data-anatomy="regua" aria-hidden="true" />
}

/** Lede — UMA frase em Schibsted Regular 17/168%, medida ≤ 65ch (verify-conteudo conta terminadores). */
export function Lede({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cx('z-lede', className)} data-anatomy="lede">{children}</p>
}

/** Prose — o campo de texto: 65ch, text-wrap pretty; nunca caixa alta em frase. */
export function Prose({ children, compacto, className, as }: { children: ReactNode; compacto?: boolean; className?: string; as?: ElementType }) {
  const Tag: ElementType = as ?? 'div'
  return <Tag className={cx('z-prose', compacto && 'z-prose--compacto', className)} data-anatomy="campo">{children}</Tag>
}

/** Etiqueta — JetBrains Mono Medium 10, caixa alta, +1,8 (p.063). O único lugar onde caixa alta é permitida. */
export function Etiqueta({ children, className, as }: { children: ReactNode; className?: string; as?: ElementType }) {
  const Tag: ElementType = as ?? 'span'
  return <Tag className={cx('z-etiqueta', className)} data-anatomy="etiqueta">{children}</Tag>
}

/** Dado — JetBrains Mono Regular 13, algarismos tabulares: todo número auditável. Nunca frase. */
export function Dado({ children, className, as }: { children: ReactNode; className?: string; as?: ElementType }) {
  const Tag: ElementType = as ?? 'span'
  return <Tag className={cx('z-dado', className)}>{children}</Tag>
}

/** Fólio — mono 13 apagado: numeração, trilha (breadcrumb), carimbo "EXEMPLO · MANUAL". */
export function Folio({ children, className, as }: { children: ReactNode; className?: string; as?: ElementType }) {
  const Tag: ElementType = as ?? 'p'
  return <Tag className={cx('z-folio', className)} data-anatomy="folio">{children}</Tag>
}
